<template>
  <section class="feature-card extra-vin-limit-card" v-loading="busy">
    <div class="section-heading">
      <div>
        <h4>SN 每日 VIN 上限</h4>
        <p>SnInfo:ExtraVinSn:&lt;SN&gt;</p>
      </div>
      <el-tag v-if="result" :type="modeTagType" effect="plain">
        {{ modeText }}
      </el-tag>
      <el-tag v-else type="info" effect="plain">待查询</el-tag>
    </div>

    <div class="query-row">
      <el-input
        v-model="sn"
        maxlength="12"
        clearable
        placeholder="输入 12 位序列号"
        :disabled="busy"
        @input="handleSnInput"
        @keyup.enter="handleQuery"
      />
      <el-button
        type="primary"
        :icon="Search"
        :loading="loading"
        :disabled="saving || deleting"
        @click="handleQuery"
      >
        查询
      </el-button>
    </div>

    <el-alert
      v-if="apiError"
      class="api-error"
      type="error"
      :closable="false"
      show-icon
      title="操作失败"
      :description="apiError"
    />

    <div v-if="result" class="result-body">
      <div class="key-row">
        <span>实际 Key</span>
        <code>{{ result.key }}</code>
      </div>

      <el-alert
        v-if="result.mode === 'invalid'"
        type="error"
        :closable="false"
        show-icon
        title="Redis 中存在异常值"
      >
        <template #default>
          原始值：<code>{{ result.rawValue || '（空字符串）' }}</code>。可直接覆盖或删除该 Key。
        </template>
      </el-alert>

      <div v-else class="current-state" :class="result.mode">
        <span>{{ result.mode === 'custom' ? '当前特殊上限' : '当前使用地区默认值' }}</span>
        <strong v-if="result.mode === 'custom'">{{ result.configuredLimit }}</strong>
        <strong v-else>
          北美 {{ result.defaults.northAmerica }} / 非北美 {{ result.defaults.nonNorthAmerica }}
        </strong>
      </div>

      <el-form label-position="top" class="limit-form">
        <el-form-item label="特殊每日 VIN 上限">
          <el-input-number
            v-model="limit"
            :min="result.limits.minimum"
            :max="result.limits.maximum"
            :step="1"
            step-strictly
            controls-position="right"
            :disabled="busy"
          />
          <div class="value-hints">
            <span>允许范围：{{ result.limits.minimum }}–{{ result.limits.maximum }}</span>
            <span>最小值由非北美默认值 + 1 计算</span>
          </div>
        </el-form-item>
      </el-form>

      <div class="defaults-row">
        <span>地区默认值</span>
        <span>北美 {{ result.defaults.northAmerica }}</span>
        <span>非北美 {{ result.defaults.nonNorthAmerica }}</span>
      </div>

      <div class="card-actions">
        <el-button
          type="danger"
          plain
          :loading="deleting"
          :disabled="!canDelete"
          @click="handleDelete"
        >
          删除并恢复默认
        </el-button>
        <el-button
          type="primary"
          :loading="saving"
          :disabled="!canSave"
          @click="handleSave"
        >
          保存并同步两端
        </el-button>
      </div>
    </div>

    <div v-else class="empty-state">
      查询序列号后，可查看、设置或删除该 SN 的每日 VIN 特殊上限。
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search } from '@element-plus/icons-vue';

import {
  deleteFordExtraVinLimit,
  getFordExtraVinLimit,
  saveFordExtraVinLimit,
} from '@/api/users';
import {
  normalizeExtraVinLimitPayload,
  validateExtraVinLimit,
  validateExtraVinSn,
} from '@/utils/fordExtraVinLimitUi.mjs';

const sn = ref('');
const queriedSn = ref('');
const result = ref(null);
const limit = ref(null);
const loading = ref(false);
const saving = ref(false);
const deleting = ref(false);
const apiError = ref('');

const busy = computed(() => loading.value || saving.value || deleting.value);
const matchesQuery = computed(() => (
  Boolean(result.value) &&
  queriedSn.value === String(sn.value || '').trim()
));
const canSave = computed(() => matchesQuery.value && Number.isInteger(limit.value));
const canDelete = computed(() => matchesQuery.value && result.value?.mode !== 'default');

const modeText = computed(() => {
  if (result.value?.mode === 'custom') return '特殊上限';
  if (result.value?.mode === 'invalid') return '异常值';
  return '地区默认';
});

const modeTagType = computed(() => {
  if (result.value?.mode === 'custom') return 'success';
  if (result.value?.mode === 'invalid') return 'danger';
  return 'info';
});

function readableApiError(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.data?.message ||
    error?.message ||
    fallback
  );
}

function clearQueryResult() {
  queriedSn.value = '';
  result.value = null;
  limit.value = null;
}

function handleSnInput(value) {
  apiError.value = '';
  if (String(value || '').trim() !== queriedSn.value) {
    clearQueryResult();
  }
}

async function handleQuery() {
  const normalizedSn = String(sn.value || '').trim();
  const validationError = validateExtraVinSn(normalizedSn);
  if (validationError) {
    apiError.value = validationError;
    ElMessage.warning(validationError);
    return false;
  }

  loading.value = true;
  apiError.value = '';
  try {
    const response = await getFordExtraVinLimit(normalizedSn);
    const nextResult = normalizeExtraVinLimitPayload(response.data);
    if (!nextResult.mode || nextResult.sn !== normalizedSn) {
      throw new Error('服务端返回的序列号限额数据不完整');
    }
    result.value = nextResult;
    queriedSn.value = nextResult.sn;
    sn.value = nextResult.sn;
    limit.value = nextResult.mode === 'custom'
      ? nextResult.configuredLimit
      : null;
    return true;
  } catch (error) {
    apiError.value = readableApiError(error, '查询每日 VIN 上限失败');
    ElMessage.error(apiError.value);
    return false;
  } finally {
    loading.value = false;
  }
}

async function handleSave() {
  if (!matchesQuery.value) {
    ElMessage.warning('请先查询当前序列号');
    return;
  }
  const validationError = validateExtraVinLimit(
    limit.value,
    result.value.limits
  );
  if (validationError) {
    ElMessage.warning(validationError);
    return;
  }

  try {
    await ElMessageBox.confirm(
      `确认将 ${queriedSn.value} 的每日 VIN 上限设置为 ${limit.value}，并同步国内和海外节点？`,
      '保存每日 VIN 上限',
      {
        confirmButtonText: '保存并同步两端',
        cancelButtonText: '取消',
        type: 'warning',
      }
    );
  } catch (error) {
    return;
  }

  saving.value = true;
  apiError.value = '';
  try {
    await saveFordExtraVinLimit(queriedSn.value, limit.value);
    const refreshed = await handleQuery();
    if (refreshed) ElMessage.success('每日 VIN 上限已保存并同步两端');
  } catch (error) {
    apiError.value = readableApiError(error, '保存每日 VIN 上限失败');
    ElMessage.error(apiError.value);
  } finally {
    saving.value = false;
  }
}

async function handleDelete() {
  if (!canDelete.value) return;

  try {
    await ElMessageBox.confirm(
      `确认删除 ${queriedSn.value} 的特殊上限？删除后将恢复北美 ${result.value.defaults.northAmerica}、非北美 ${result.value.defaults.nonNorthAmerica}。`,
      '删除每日 VIN 上限',
      {
        confirmButtonText: '删除并恢复默认',
        cancelButtonText: '取消',
        type: 'warning',
      }
    );
  } catch (error) {
    return;
  }

  deleting.value = true;
  apiError.value = '';
  try {
    await deleteFordExtraVinLimit(queriedSn.value);
    limit.value = null;
    const refreshed = await handleQuery();
    if (refreshed) ElMessage.success('特殊上限已删除，当前使用地区默认值');
  } catch (error) {
    apiError.value = readableApiError(error, '删除每日 VIN 上限失败');
    ElMessage.error(apiError.value);
  } finally {
    deleting.value = false;
  }
}
</script>

<style scoped>
.feature-card {
  min-width: 0;
  padding: 16px;
  border: 1px solid #b8d7f5;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(31, 56, 88, 0.05);
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-heading h4 {
  margin: 0;
  color: #132033;
  font-size: 16px;
  font-weight: 800;
}

.section-heading p {
  margin: 5px 0 0;
  color: #6b7b8f;
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
}

.query-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
}

.api-error {
  margin-top: 12px;
}

.result-body {
  display: grid;
  gap: 12px;
  margin-top: 14px;
}

.key-row,
.defaults-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  color: #64748b;
  font-size: 12px;
}

.key-row code {
  min-width: 0;
  overflow: hidden;
  color: #24496e;
  font-family: Consolas, Monaco, monospace;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.current-state {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid #d8e9fb;
  border-radius: 8px;
  color: #31506f;
  background: #f7fbff;
}

.current-state.custom {
  border-color: #bbf7d0;
  color: #166534;
  background: #f0fdf4;
}

.current-state strong {
  text-align: right;
}

.limit-form :deep(.el-form-item) {
  margin-bottom: 0;
}

.limit-form :deep(.el-form-item__label) {
  color: #334155;
  font-weight: 800;
}

.limit-form :deep(.el-input-number) {
  width: 100%;
}

.value-hints {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 6px;
  color: #7a8a9d;
  font-size: 12px;
  line-height: 1.4;
}

.defaults-row {
  justify-content: flex-start;
  padding: 9px 10px;
  border-radius: 7px;
  background: #f8fafc;
}

.defaults-row span:first-child {
  margin-right: auto;
  color: #334155;
  font-weight: 800;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.empty-state {
  min-height: 112px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  border: 1px dashed #c8d6e6;
  border-radius: 8px;
  color: #7a8a9d;
  background: #f8fafc;
  font-size: 13px;
  line-height: 1.6;
  text-align: center;
}

@media (max-width: 560px) {
  .section-heading,
  .key-row,
  .current-state,
  .defaults-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .query-row {
    grid-template-columns: 1fr;
  }

  .value-hints,
  .card-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .card-actions .el-button {
    width: 100%;
    margin-left: 0;
  }
}
</style>
